import type { ID } from '@octopus-do/octopus-project-core'
import { LoaderActions } from '@store/loaders'
import type { LoaderType } from '@store/loaders/types'
import { SeoActions } from '@store/reducers/seo/actions'
import type { SeoFormValues } from '@store/reducers/seo/types'
import type { AppState } from '@store/types'
import { UploadResultActions } from '@store/uploadResultSaga'
import { notificationActionCreators } from '@storeOld/reducers/notification'
import { call, put, select } from 'redux-saga/effects'

import { importSeoTags } from '@/services/importSeoTags'

type ImportedSeoTags = Awaited<ReturnType<typeof importSeoTags>>

// Лоадер привязан к узлу: синхронизация одной страницы не должна крутить
// иконку в меню другой.
export const seoSyncLoader = (nodeId: ID): LoaderType => `seo_sync:${nodeId}`

const setErrorNotification = () =>
  put(
    notificationActionCreators.setCustomNotification({
      text: 'Something has gone wrong. Please try again later.',
      toastContainerId: 'secondary',
      type: 'coral',
    })
  )

/**
 * OCT-5177: «Sync from URL» тянет SEO-теги по адресу страницы. Пока идёт
 * запрос, пункт меню крутит иконку — отсюда общий лоадер `seo_sync`.
 */
export function* syncFromUrl({
  payload,
}: ReturnType<typeof SeoActions.syncFromUrl>) {
  const { id, projectId, url } = payload

  yield put(LoaderActions.set(seoSyncLoader(id)))

  try {
    const data: ImportedSeoTags = yield call(importSeoTags, projectId, url)

    if (!data) {
      yield setErrorNotification()
      return
    }

    // Форму читаем на момент ответа, а не запроса: пока он шёл, пользователь
    // мог править поля.
    const form: SeoFormValues = yield select((state: AppState) => state.seoForm)

    const image = data.image
      ? { id: data.image.id, value: data.image.downloadUrl }
      : null

    yield put(
      SeoActions.change({
        id,
        form: {
          ...form,
          title: data.title || '',
          description: data.description || '',
          slug: data.slug || '',
          h1_header: data.h1 || '',
          image,
        },
      })
    )

    if (data.image) {
      yield put(UploadResultActions.success(data.image))
    }

    yield put(
      notificationActionCreators.setClearNotification({
        toastContainerId: 'secondary',
      })
    )
  } catch {
    yield setErrorNotification()
  } finally {
    yield put(LoaderActions.unset(seoSyncLoader(id)))
  }
}
